import {
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid/models';
import { z } from 'zod';
import { invert } from 'es-toolkit';
import { CONFIG } from '@/config.ts';

// Utils to adapt grid models to Sieve request params
// More info here: https://github.com/Biarity/Sieve?tab=readme-ov-file#send-a-request

// MUI Filter Operators
export type MuiFilterOperator =
  | 'contains'
  | 'doesNotContain'
  | 'equals'
  | 'doesNotEqual'
  | 'startsWith'
  | 'endsWith'
  | '='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | 'is'
  | 'not'
  | 'after'
  | 'onOrAfter'
  | 'before'
  | 'onOrBefore'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'isAnyOf';

// Sieve Filter Operators (only those mapped from MUI)
export type SieveFilterOperator =
  | '=='
  | '!='
  | '>'
  | '<'
  | '>='
  | '<='
  | '@='
  | '_='
  | '_-='
  | '!@=';

/**
 * Mapping from MUI filter operators to Sieve filter operators
 * Note: Some operators like 'isEmpty', 'isNotEmpty', and 'isAnyOf' may require special handling
 */
export const muiToSieveOperatorMap: Record<MuiFilterOperator, SieveFilterOperator> = {
  // String operations
  contains: '@=',
  doesNotContain: '!@=',
  startsWith: '_=',
  endsWith: '_-=',

  // Equality operations
  equals: '==',
  doesNotEqual: '!=',
  '=': '==',
  '!=': '!=',
  is: '==',
  not: '!=',
  isEmpty: '==', // Requires special handling for null/empty values
  isNotEmpty: '!=', // Requires special handling for null/empty values
  isAnyOf: '==', // Requires special handling for OR logic

  // Comparison operations
  '>': '>',
  '>=': '>=',
  '<': '<',
  '<=': '<=',

  // Date operations (map to comparison operators)
  after: '>',
  onOrAfter: '>=',
  before: '<',
  onOrBefore: '<=',
};

/**
 * Reverse mapping from Sieve filter operators to MUI filter operators
 * Note: When multiple MUI operators map to the same Sieve operator,
 * this will contain the more descriptive string version
 */
export const sieveToMuiOperatorMap: Record<SieveFilterOperator, MuiFilterOperator> =
  invert(muiToSieveOperatorMap);

export interface GridState {
  sorts?: GridSortModel;
  filters?: GridFilterModel;
  pagination?: GridPaginationModel;
}

export interface SieveParams {
  sorts?: string;
  filters?: string;
  page?: number;
  pageSize?: number;
}

export const gridStateSearchSchema = z.object({
  sorts: z.string().optional().catch(undefined),
  filters: z.string().optional().catch(undefined),
  page: z.coerce.number().optional().catch(undefined),
  pageSize: z.coerce.number().optional().catch(undefined),
});

/**
 * Escapes special characters in filter values for Sieve format
 * Escapes: backslash (\), pipe (|), comma (,), and string "null"
 * Converts null/undefined to null
 */
function escapeFilterValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'string') {
    const strValue = String(value);
    // Special case: if the string is exactly "null", escape it
    if (strValue === 'null') {
      return '\\null';
    }
    // Escape special characters: backslash first, then pipe and comma
    return strValue
      .replace(/\\/g, '\\\\') // Escape backslash first
      .replace(/\|/g, '\\|') // Escape pipe
      .replace(/,/g, '\\,'); // Escape comma
  }

  return `${value}`;
}

/**
 * Converts a single filter item to Sieve format
 * Returns null for unknown operators or undefined values
 */
function convertFilterItemToSieve(item: GridFilterModel['items'][number]): string | null {
  const operator = item.operator;

  // Special handling for isEmpty - use ==null
  if (operator === 'isEmpty') {
    return `${item.field}==null`;
  }

  // Special handling for isNotEmpty - use !=null
  if (operator === 'isNotEmpty') {
    return `${item.field}!=null`;
  }

  // Discard filter items with undefined values
  if (item.value === undefined) {
    return null;
  }

  // Special handling for isAnyOf - create pipe-separated values without repeating field name
  if (operator === 'isAnyOf' && Array.isArray(item.value)) {
    const escapedValues = item.value.map((val) => escapeFilterValue(val)).join('|');
    return `${item.field}==${escapedValues}`;
  }

  // Get the Sieve operator
  if (!Object.keys(muiToSieveOperatorMap).includes(operator)) {
    console.warn(`Unknown operator: ${operator}`);
    return null;
  }

  const sieveOperator = muiToSieveOperatorMap[operator as MuiFilterOperator];
  const escapedValue = escapeFilterValue(item.value);
  return `${item.field}${sieveOperator}${escapedValue}`;
}

/**
 * Converts GridFilterModel to Sieve filter string
 * Validates that OR logic operator is not used
 */
function convertFiltersToSieve(filters?: GridFilterModel): string | undefined {
  if (!filters?.items || filters.items.length === 0) {
    return undefined;
  }

  // OR logic operator is not supported
  if (filters.logicOperator === 'or') {
    throw new Error(
      'OR logic operator is not supported. Use isAnyOf operator for OR conditions on a single field.'
    );
  }

  const filterStrings = filters.items.map((item) => convertFilterItemToSieve(item)).filter(Boolean); // Remove null entries from unknown operators

  if (filterStrings.length === 0) {
    return undefined;
  }

  return filterStrings.join(','); // Always use AND (comma) between different filters
}

/**
 * Converts GridSortModel to Sieve sort string
 */
function convertSortsToSieve(sorts?: GridSortModel): string | undefined {
  if (!sorts || sorts.length === 0) {
    return undefined;
  }

  return sorts.map((sort) => `${sort.sort === 'desc' ? '-' : ''}${sort.field}`).join(',');
}

/**
 * Converts GridPaginationModel to Sieve pagination params
 * MUI Grid uses 0-indexed pages, Sieve uses 1-indexed pages
 */
function convertPaginationToSieve(pagination?: GridPaginationModel): {
  page?: number;
  pageSize?: number;
} {
  if (!pagination) {
    return {};
  }

  return {
    page: pagination.page + 1, // Convert from 0-indexed to 1-indexed
    pageSize: pagination.pageSize,
  };
}

export function gridStateToSieveParams(gridState: GridState): SieveParams {
  const paginationParams = convertPaginationToSieve(gridState.pagination);

  return {
    sorts: convertSortsToSieve(gridState.sorts),
    filters: convertFiltersToSieve(gridState.filters),
    ...paginationParams,
  };
}

/**
 * Unescapes special characters in filter values from Sieve format
 * Unescapes: backslash (\), pipe (|), comma (,), and escaped string "\null"
 * Converts null to null
 */
function unescapeFilterValue(value: string): string | null {
  // Handle "null" → null conversion first
  if (value === 'null') {
    return null;
  }

  // Handle "\null" → string "null" conversion
  if (value === '\\null') {
    return 'null';
  }

  return value
    .replace(/\\,/g, ',') // Unescape comma
    .replace(/\\\|/g, '|') // Unescape pipe
    .replace(/\\\\/g, '\\'); // Unescape backslash (must be last)
}

/**
 * Parses a single Sieve filter string into a filter item
 * Returns null if the operator is not recognized
 */
function parseSieveFilterItem(filterStr: string): GridFilterModel['items'][number] | null {
  const trimmed = filterStr.trim();

  // Try to match operators (order matters - match longer operators first)
  const operatorPatterns: Array<{ regex: RegExp; operator: MuiFilterOperator }> = [
    { regex: /^([^!=<>@_]+)(!=null)/, operator: 'isNotEmpty' },
    { regex: /^([^!=<>@_]+)(==null)/, operator: 'isEmpty' },
    { regex: /^([^!=<>@_]+)(!@=)(.+)$/, operator: 'doesNotContain' },
    { regex: /^([^!=<>@_]+)(_-=)(.+)$/, operator: 'endsWith' },
    { regex: /^([^!=<>@_]+)(_=)(.+)$/, operator: 'startsWith' },
    { regex: /^([^!=<>@_]+)(@=)(.+)$/, operator: 'contains' },
    { regex: /^([^!=<>@_]+)(>=)(.+)$/, operator: '>=' },
    { regex: /^([^!=<>@_]+)(<=)(.+)$/, operator: '<=' },
    { regex: /^([^!=<>@_]+)(!=)(.+)$/, operator: '!=' },
    { regex: /^([^!=<>@_]+)(==)(.+)$/, operator: 'equals' },
    { regex: /^([^!=<>@_]+)(>)(.+)$/, operator: '>' },
    { regex: /^([^!=<>@_]+)(<)(.+)$/, operator: '<' },
  ];

  for (const { regex, operator } of operatorPatterns) {
    const match = trimmed.match(regex);
    if (match) {
      const field = match[1].trim();

      // Handle isEmpty and isNotEmpty specially
      if (operator === 'isEmpty' || operator === 'isNotEmpty') {
        return { field, operator, value: null };
      }

      const rawValue = match[3];

      // Check if this is an isAnyOf (pipe-separated values)
      // Only treat as isAnyOf if there are unescaped pipes
      if (operator === 'equals' && /(?<!\\)\|/.test(rawValue)) {
        // Split by unescaped pipes
        const values = rawValue.split(/(?<!\\)\|/).map((v) => unescapeFilterValue(v));
        return { field, operator: 'isAnyOf', value: values };
      }

      const value = unescapeFilterValue(rawValue);
      return { field, operator, value };
    }
  }

  console.warn(`Unable to parse filter: ${filterStr}`);
  return null;
}

/**
 * Converts Sieve filter string to GridFilterModel
 */
function convertSieveFiltersToGrid(
  filters?: string,
  filterableFields?: string[]
): GridFilterModel | undefined {
  if (!filters || filters.trim() === '') {
    return undefined;
  }

  // Split by unescaped commas
  const filterStrings = filters.split(/(?<!\\),/);

  let items = filterStrings
    .map((str) => parseSieveFilterItem(str))
    .filter(Boolean) as GridFilterModel['items'];

  // Filter by allowed fields if specified
  if (filterableFields) {
    items = items.filter((item) => filterableFields.includes(item.field));
  }

  if (items.length === 0) {
    return undefined;
  }

  return {
    items,
    logicOperator: GridLogicOperator.And, // Always AND for Sieve
  };
}

/**
 * Converts Sieve sort string to GridSortModel
 */
function convertSieveSortsToGrid(
  sorts?: string,
  sortableFields?: string[]
): GridSortModel | undefined {
  if (!sorts || sorts.trim() === '') {
    return undefined;
  }

  let sortModel: GridSortModel = sorts.split(',').map((sortStr) => {
    const trimmed = sortStr.trim();
    const isDesc = trimmed.startsWith('-');
    const field = isDesc ? trimmed.slice(1) : trimmed;
    return {
      field,
      sort: isDesc ? 'desc' : 'asc',
    };
  });

  // Filter out unknown fields if sortableFields is provided
  if (sortableFields) {
    sortModel = sortModel.filter((sort) => sortableFields.includes(sort.field));
  }

  return sortModel.length > 0 ? sortModel : undefined;
}

/**
 * Converts Sieve pagination params to GridPaginationModel
 * Sieve uses 1-indexed pages, MUI Grid uses 0-indexed pages
 */
function convertSievePaginationToGrid(
  page?: number,
  pageSize?: number
): GridPaginationModel | undefined {
  // Both page and pageSize must be present for a valid pagination model
  if (page === undefined || pageSize === undefined) {
    return undefined;
  }

  return {
    page: Math.max(0, page - 1), // Convert from 1-indexed to 0-indexed (ensure non-negative)
    pageSize,
  };
}

export function sieveParamsToGridState(
  sieveParams: SieveParams,
  options?: { sortableFields?: string[]; filterableFields?: string[]; defaultPageSize?: number }
): GridState {
  const sorts = convertSieveSortsToGrid(sieveParams.sorts, options?.sortableFields);
  const filters = convertSieveFiltersToGrid(sieveParams.filters, options?.filterableFields);
  const pagination = convertSievePaginationToGrid(sieveParams.page, sieveParams.pageSize);

  const result: GridState = {};

  if (sorts && sorts.length > 0) {
    result.sorts = sorts;
  }

  if (filters) {
    result.filters = filters;
  }

  if (pagination) {
    result.pagination = pagination;
  } else {
    result.pagination = {
      page: 0,
      pageSize: options?.defaultPageSize ?? CONFIG.defaultPageSize,
    };
  }

  return result;
}
